import Settings from '@/utils/settings'
import { snakify, kebabify } from '@/utils/helpers'
import { defaultQueryParams } from '@/utils/constants'
import axios from 'axios'

const { endpoints } = Settings()

// Local API functions

function serializeOutData (data) {
  let outData = {}
  Object.keys(data).forEach(field => {
    if (field === 'short_title') {
      data[field] = kebabify(data[field])
    }
    outData[snakify(field)] = data[field]
  })
  return JSON.stringify(outData)
}

function queryParams (params) {
  params = params || {}
  return Object.keys(params)
    .filter(k => params[k])
    .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(params[k]))
    .join('&')
}

async function callApi (url, method, data, headers = {}) {
  const options = {
    url,
    method,
    headers
  }
  const token = localStorage.getItem('user-token')
  if (token) {
    options.headers['Authorization'] = `Token ${token}`
  }
  if (data && data instanceof FormData) {
    options.data = data
  } else if (data) {
    options.headers['Content-Type'] = 'application/json'
    options.data = serializeOutData(data)
  }
  await sleep(0)
  return new Promise((resolve, reject) => {
    axios(options)
      .then(response => resolve(response.data))
      .catch((err) => reject(err))
  })
}

function sleep (ms) {
  ms |= Math.floor(Math.random() * 20)
  return new Promise(resolve => setTimeout(resolve, ms))
}

// Public API functions

export const fetchListRecords = async (model, params) => {
  params = {
    ...params,
    ...(defaultQueryParams[model])
  }
  const endpoint = endpoints[model]
  const url = `${endpoint}?${queryParams(params)}`
  await sleep()
  return callApi(url, 'GET')
}

export const fetchRecord = (model, recordId, params) => {
  const endpoint = endpoints[model]
  const url = `${endpoint}/${recordId}?${queryParams(params)}`
  return callApi(url, 'GET')
}

export const createRecord = (model, data) => {
  const endpoint = endpoints[model]
  return callApi(endpoint, 'POST', data)
}

export const updateRecord = (model, recordId, data) => {
  const endpoint = endpoints[model]
  const url = `${endpoint}/${recordId}`
  return callApi(url, 'PATCH', data)
}

// Specific Submission Endpoints

export const uploadSubmissionForm = (submissionId, data) => {
  const endpoint = endpoints['submissions']
  const url = `${endpoint}/${submissionId}`
  return callApi(url, 'PATCH', data)
}

export const processSubmissionForm = (submissionId, data) => {
  const endpoint = endpoints['submissions']
  const url = `${endpoint}/process/${submissionId}`
  return callApi(url, 'POST', data)
}

export const downloadSubmissionForm = () => {
  const endpoint = endpoints['submissions']
  return `${endpoint}/download`
}

export const deleteSubmission = (submissionId) => {
  const endpoint = endpoints['submissions']
  const url = `${endpoint}/${submissionId}`
  return callApi(url, 'DELETE')
}

// User Profile Endpoints

export const fetchUserProfile = () => {
  const endpoint = endpoints['user']
  return callApi(endpoint, 'GET')
}

export const updateUserProfile = (data) => {
  const endpoint = endpoints['user']
  return callApi(endpoint, 'PUT', data)
}

export const changePassword = (data) => {
  const endpoint = endpoints['password']
  const url = `${endpoint}change/`
  return callApi(url, 'POST', data)
}

export const resetPassword = (data) => {
  const endpoint = endpoints['password']
  const url = `${endpoint}reset/`
  return callApi(url, 'POST', data)
}

export const verifyEmail = (key) => {
  const endpoint = endpoints['signup']
  const url = `${endpoint}account-confirm-email/${key}/`
  return callApi(url, 'GET')
}

// Specific bioModel Query

export const fetchIndividualTree = (individualId) => {
  const endpoint = endpoints['individuals']
  const url = `${endpoint}/tree/${individualId}`
  return callApi(url, 'GET')
}

// Specific Third Party Integrations

export const getJiraEpic = (key) => {
  const endpoint = endpoints['projects']
  const url = `${endpoint}/epic/${key}`
  return callApi(url, 'GET')
}

export const createJiraTicket = (key, data) => {
  const endpoint = endpoints['projects']
  const url = `${endpoint}/ticket/${key}`
  return callApi(url, 'POST', data)
}
